export type ToastKind = 'success' | 'error';

export interface ToastMessage {
  id: number;
  kind: ToastKind;
  message: string;
}

type Listener = (toast: ToastMessage) => void;

/** A burst of saves (a drag, a row of toggles) shows one confirmation, not one per call. */
const SAVED_DEBOUNCE_MS = 350;

const listeners = new Set<Listener>();
let nextId = 1;
let savedTimer: number | null = null;

export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function cancelPendingSaved(): void {
  if (savedTimer !== null) {
    window.clearTimeout(savedTimer);
    savedTimer = null;
  }
}

function emit(kind: ToastKind, message: string): void {
  const toast = { id: nextId++, kind, message };
  listeners.forEach((l) => l(toast));
}

export const toast = {
  // A specific message wins over the generic "saved" that may be waiting for the same action.
  success: (message: string) => {
    cancelPendingSaved();
    emit('success', message);
  },
  error: (message: string) => {
    cancelPendingSaved();
    emit('error', message);
  },
  /** Generic confirmation of a save, debounced; dropped if a specific toast follows within the window. */
  saved: (message: string) => {
    cancelPendingSaved();
    savedTimer = window.setTimeout(() => {
      savedTimer = null;
      emit('success', message);
    }, SAVED_DEBOUNCE_MS);
  },
};
